/**
 * Typed fetch helpers for the app's own API.
 *
 * Responses are parsed with Zod rather than cast, so a contract change surfaces
 * at the boundary instead of as a confusing render failure three components
 * deep. Errors carry the API's machine-readable code so callers can react to a
 * specific failure rather than matching on message text.
 */
import { z } from 'zod';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseResponse<T extends z.ZodType>(
  response: Response,
  schema: T,
): Promise<z.infer<T>> {
  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = z.object({ error: z.string(), message: z.string().optional() }).safeParse(body);
    throw new ApiError(
      response.status,
      parsed.success ? parsed.data.error : 'unknown_error',
      parsed.success ? (parsed.data.message ?? parsed.data.error) : 'Request failed',
    );
  }

  return schema.parse(body);
}

async function request<T extends z.ZodType>(
  path: string,
  schema: T,
  init?: RequestInit,
): Promise<z.infer<T>> {
  const response = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  return parseResponse(response, schema);
}

/**
 * A multipart upload — deliberately not `request`: that helper always sets
 * `content-type: application/json`, which would stop the browser from
 * setting its own `multipart/form-data; boundary=...` header for a
 * `FormData` body.
 */
async function requestMultipart<T extends z.ZodType>(
  path: string,
  schema: T,
  formData: FormData,
): Promise<z.infer<T>> {
  const response = await fetch(path, { method: 'POST', body: formData });
  return parseResponse(response, schema);
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const competitionSchema = z.object({
  id: z.string(),
  slug: z.string(),
  displayName: z.string(),
  sportId: z.string(),
  scoreable: z.boolean(),
});

export const groupSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Purely decorative cover image (PLAN.md Phase 10.19). Null until the organizer uploads one. */
  imageUrl: z.string().nullable(),
  memberCount: z.number(),
  poolCount: z.number(),
  isOrganizer: z.boolean(),
});

/**
 * A circle: roster + one shared invite link. No competition data — that
 * moved to a bolão, which now belongs to a circle rather than being the
 * circle itself. See `poolDetailSchema` for the competition-scoped shape.
 */
export const groupDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  memberCount: z.number(),
  isOrganizer: z.boolean(),
  roster: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
      isOwner: z.boolean(),
      isYou: z.boolean(),
      avatarUrl: z.string().nullable(),
      /** Playing by link and PIN, without an account (PLAN.md Phase 10.29). */
      isGuest: z.boolean(),
      /** Guests only: false once an organizer has reset their PIN, until they choose a new one. */
      pinSet: z.boolean(),
      joinedAt: z.string(),
    }),
  ),
  /** Organizer-only fields; absent for ordinary members. */
  linkSharingEnabled: z.boolean().optional(),
  /** Organizer-only: the actual join token, always readable — not just at creation/regeneration time. Null when link sharing is off. */
  joinToken: z.string().nullable().optional(),
  pendingInvites: z
    .array(
      z.object({
        id: z.string(),
        email: z.string(),
        expiresAt: z.string().nullable(),
        createdAt: z.string(),
      }),
    )
    .optional(),
});

/** One bolão in a circle's `GET /groups/:id/bolaos` list. */
export const poolSummarySchema = z.object({
  id: z.string(),
  groupId: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  competitionSlug: z.string(),
  competitionDisplayName: z.string(),
  sportId: z.string(),
  participantCount: z.number(),
  /** Whether the viewer already has a participant row in this specific bolão. */
  isPlaying: z.boolean(),
});

/** One bolão's own detail — the competition + roster-membership context the old `GroupDetail` used to carry. */
export const poolDetailSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  competitionId: z.string(),
  competitionSlug: z.string(),
  competitionDisplayName: z.string(),
  sportId: z.string(),
  isOrganizer: z.boolean(),
  isPlaying: z.boolean(),
  participantCount: z.number(),
  /** Whether a participant may lock a football prediction "forever" — organizer-controlled, see `createCompetition.allowLockForeverLabel`. */
  allowLockForever: z.boolean(),
});

/** What a join link points at, shown before signing in. */
export const joinPreviewSchema = z.object({
  groupId: z.string(),
  groupName: z.string(),
  organizerName: z.string(),
  memberCount: z.number(),
  poolCount: z.number(),
  alreadyMember: z.boolean(),
});

/**
 * The circle's roster as shown to someone who chose to play without an
 * account — the "pick yourself off the list" screen (PLAN.md Phase 10.29).
 *
 * Reachable with no session at all; the invite token is what gates it.
 */
export const guestRosterSchema = z.object({
  groupId: z.string(),
  groupName: z.string(),
  members: z.array(
    z.object({
      memberId: z.string(),
      displayName: z.string(),
      avatarUrl: z.string().nullable(),
      /** Signs in with a real account, so this entry is shown but never selectable here. */
      hasAccount: z.boolean(),
      /** False for a guest entry waiting to be claimed — no PIN chosen yet, or an organizer reset it. */
      pinSet: z.boolean(),
    }),
  ),
});

/** Who this browser is signed in as without an account, if anyone. */
export const guestSessionSchema = z.object({
  guest: z
    .object({
      userId: z.string(),
      name: z.string(),
      image: z.string().nullable(),
      /** The single circle a guest may reach. */
      groupId: z.string(),
    })
    .nullable(),
});

export const pendingInviteSchema = z.object({
  inviteId: z.string(),
  groupId: z.string(),
  groupName: z.string(),
  invitedByName: z.string(),
});

const teamSchema = z.object({
  code: z.string(),
  flagCode: z.string().nullable(),
  crestUrl: z.string().nullable(),
  name: z.string(),
});

/**
 * The signed-in home screen's aggregate (PLAN.md Phase 10.27) — everything
 * the screen shows across every bolão the viewer plays, in one response.
 */
const homePickSchema = z.object({
  bolaoId: z.string(),
  groupId: z.string(),
  bolaoName: z.string(),
  matchId: z.string(),
  homeTeam: teamSchema.nullable(),
  awayTeam: teamSchema.nullable(),
  kickoffAt: z.string().nullable(),
});

const homeResultSchema = z.object({
  bolaoId: z.string(),
  groupId: z.string(),
  bolaoName: z.string(),
  matchId: z.string(),
  homeTeam: teamSchema.nullable(),
  awayTeam: teamSchema.nullable(),
  realHome: z.number().nullable(),
  realAway: z.number().nullable(),
  predictedHome: z.number(),
  predictedAway: z.number(),
  points: z.number(),
  computedAt: z.string(),
  isNew: z.boolean(),
});

const homeStandingSchema = z.object({
  bolaoId: z.string(),
  groupId: z.string(),
  bolaoName: z.string(),
  groupName: z.string(),
  sportId: z.string(),
  position: z.number(),
  previousPosition: z.number().nullable(),
  points: z.number(),
  participantCount: z.number(),
});

export const homeSummarySchema = z.object({
  needsPicks: z.array(homePickSchema),
  needsPicksTotal: z.number(),
  nextDeadlineAt: z.string().nullable(),
  recentResults: z.array(homeResultSchema),
  newResultCount: z.number(),
  standings: z.array(homeStandingSchema),
});

const editabilitySchema = z.union([
  z.object({ editable: z.literal(true) }),
  z.object({
    editable: z.literal(false),
    reason: z.enum(['locked_by_participant', 'kickoff_passed', 'match_finished']),
  }),
]);

export const predictionMatchSchema = z.object({
  id: z.string(),
  matchNumber: z.number(),
  stageSlug: z.string(),
  stageName: z.string(),
  homeTeam: teamSchema.nullable(),
  awayTeam: teamSchema.nullable(),
  kickoffAt: z.string().nullable(),
  venue: z.string().nullable(),
  status: z.enum(['scheduled', 'live', 'finished', 'postponed', 'cancelled']),
  realHome: z.number().nullable(),
  realAway: z.number().nullable(),
  realPenaltyWinner: z.enum(['home', 'away']).nullable(),
  canGoToPenalties: z.boolean(),
  /**
   * The pot, flat awards, and shootout values this match was (or, before
   * it's finished, currently would be) scored under — always present, so
   * "See points details" can show it before kickoff, not just after.
   */
  matchParams: z.object({
    outcome: z.number(),
    exact: z.number(),
    pot: z.number(),
    decay: z.number(),
    threshold: z.number(),
    penaltyExact: z.number(),
    penaltyWrong: z.number(),
  }),
  prediction: z
    .object({
      home: z.number(),
      away: z.number(),
      penaltyWinner: z.enum(['home', 'away']).nullable(),
      lockedAt: z.string().nullable(),
    })
    .nullable(),
  editability: editabilitySchema,
  revealed: z.array(
    z.object({
      participantId: z.string(),
      displayName: z.string(),
      image: z.string().nullable(),
      home: z.number(),
      away: z.number(),
      penaltyWinner: z.enum(['home', 'away']).nullable(),
      score: z
        .object({
          outcome: z.number(),
          exact: z.number(),
          proximity: z.number(),
          penalty: z.number(),
          total: z.number(),
        })
        .nullable(),
    }),
  ),
});

export const predictionsForGroupSchema = z.object({
  isParticipant: z.boolean(),
  matches: z.array(predictionMatchSchema),
});

export const leaderboardRowSchema = z.object({
  participantId: z.string(),
  displayName: z.string(),
  isYou: z.boolean(),
  isOwner: z.boolean(),
  image: z.string().nullable(),
  rank: z.number(),
  total: z.number(),
  outcome: z.number(),
  exact: z.number(),
  proximity: z.number(),
  penalty: z.number(),
  tier: z.enum(['hot', 'rising', 'steady', 'cold']),
});

export const customLeaderboardRowSchema = z.object({
  participantId: z.string(),
  displayName: z.string(),
  isYou: z.boolean(),
  isOwner: z.boolean(),
  image: z.string().nullable(),
  rank: z.number(),
  total: z.number(),
  correctCount: z.number(),
  outcome: z.number(),
  exact: z.number(),
  proximity: z.number(),
  penalty: z.number(),
  otherPoints: z.number(),
  tier: z.enum(['hot', 'rising', 'steady', 'cold']),
});

export const customLeaderboardSchema = z.object({
  rows: z.array(customLeaderboardRowSchema),
});

export const leaderboardSchema = z.object({
  rows: z.array(leaderboardRowSchema),
});

export const profileMatchSchema = z.object({
  id: z.string(),
  matchNumber: z.number(),
  stageSlug: z.string(),
  stageName: z.string(),
  homeTeam: teamSchema.nullable(),
  awayTeam: teamSchema.nullable(),
  kickoffAt: z.string().nullable(),
  venue: z.string().nullable(),
  status: z.enum(['scheduled', 'live', 'finished', 'postponed', 'cancelled']),
  canGoToPenalties: z.boolean(),
  prediction: z.object({
    home: z.number(),
    away: z.number(),
    penaltyWinner: z.enum(['home', 'away']).nullable(),
    lockedAt: z.string().nullable(),
  }),
  realHome: z.number().nullable(),
  realAway: z.number().nullable(),
  realPenaltyWinner: z.enum(['home', 'away']).nullable(),
  score: z
    .object({
      outcome: z.number(),
      exact: z.number(),
      proximity: z.number(),
      penalty: z.number(),
      total: z.number(),
    })
    .nullable(),
});

export const participantProfileSchema = z.object({
  participantId: z.string(),
  displayName: z.string(),
  isOwner: z.boolean(),
  image: z.string().nullable(),
  matches: z.array(profileMatchSchema),
});

export const pointsEvolutionSchema = z.object({
  matchdays: z.array(
    z.object({ matchId: z.string(), matchNumber: z.number(), stageSlug: z.string() }),
  ),
  series: z.array(
    z.object({ participantId: z.string(), displayName: z.string(), points: z.array(z.number()) }),
  ),
});

export const matchParamsSchema = z.object({
  outcome: z.number(),
  exact: z.number(),
  pot: z.number(),
  decay: z.number(),
  threshold: z.number(),
  penaltyExact: z.number(),
  penaltyWrong: z.number(),
});

export const rulesStageSchema = z.object({
  slug: z.string(),
  name: z.string(),
  params: matchParamsSchema,
  canGoToPenalties: z.boolean(),
});

export const rulesWorkedPredictionSchema = z.object({
  label: z.string(),
  home: z.number(),
  away: z.number(),
  penaltyWinner: z.enum(['home', 'away']).nullable(),
  points: z.object({
    outcome: z.number(),
    exact: z.number(),
    proximity: z.number(),
    penalty: z.number(),
    total: z.number(),
  }),
});

export const rulesExampleSchema = z.object({
  stageSlug: z.string(),
  params: matchParamsSchema,
  real: z.object({
    home: z.number(),
    away: z.number(),
    penaltyWinner: z.enum(['home', 'away']).nullable(),
  }),
  predictions: z.array(rulesWorkedPredictionSchema),
});

export const groupRulesSchema = z.object({
  competitionSlug: z.string(),
  participantCount: z.number(),
  stages: z.array(rulesStageSchema),
  groupStageExample: rulesExampleSchema,
  knockoutExample: rulesExampleSchema,
  proximityExample: rulesExampleSchema,
});

// --- custom competitions (PLAN.md Phase 10.3/10.4) --------------------------
//
// NOT A QUIZ: the organizer does not know the correct answer when creating
// a question. `resultStatus`/`pointsAwarded`/`isWinner` stay null/false
// until the organizer reveals results (`revealedAt` is set) — see
// apps/api/src/custom/service.ts's module doc comment.

export const scoringModeSchema = z.enum(['per_answer', 'winner_loser']);
export const resultStatusSchema = z.enum(['correct', 'partial', 'wrong']);
export const questionKindSchema = z.enum(['multiple_choice', 'football', 'date']);
export const penaltyWinnerSchema = z.enum(['home', 'away']);

export const questionDisplayFormatSchema = z.enum(['list', 'card']);
/** How a question looks once revealed — see the `question_results_format` enum's doc comment. */
export const questionResultsFormatSchema = z.enum(['list', 'panels', 'leaderboard']);
/** How a `date` question turns "who guessed closest" into points — see the `date_scoring_mode` enum's doc comment. */
export const dateScoringModeSchema = z.enum([
  'placement',
  'ladder',
  'closeness',
  'winner_takes_all',
]);

export const questionOptionSchema = z.object({
  id: z.string(),
  optionNumber: z.number(),
  label: z.string(),
  resultStatus: resultStatusSchema.nullable(),
  pointsAwarded: z.number().nullable(),
  isWinner: z.boolean(),
  /** Cosmetic, only rendered in `card` display format. */
  color: z.string().nullable(),
  emoji: z.string().nullable(),
  imageUrl: z.string().nullable(),
});

export const questionRevealedPickSchema = z.object({
  participantId: z.string(),
  displayName: z.string(),
  image: z.string().nullable(),
  /** Set for a `multiple_choice` pick. */
  optionId: z.string().nullable(),
  /** Set for a `football` pick. */
  homeScore: z.number().nullable(),
  awayScore: z.number().nullable(),
  penaltyWinner: penaltyWinnerSchema.nullable(),
  /** Set for a `date` pick — `YYYY-MM-DD`, the day that was tapped. */
  dateGuess: z.string().nullable(),

  // --- `date` standings; null until the question has a real result ---------
  //
  // Server-computed, never re-derived here: rank, gap and points are one
  // ruling about who won, made in `apps/api/src/custom/date-scoring.ts`, and a
  // second implementation in the browser could disagree with the scores
  // already stored at reveal.
  /** Whole minutes between this guess (counted as noon) and the real moment. */
  deltaMinutes: z.number().nullable(),
  /** 1-based; tied guesses share a rank. */
  rank: z.number().nullable(),
  /** What this placing earned under the question's `dateScoringMode`. 0 for `placement`. */
  points: z.number().nullable(),
});

export const questionSchema = z.object({
  id: z.string(),
  questionNumber: z.number(),
  kind: questionKindSchema,
  prompt: z.string(),
  deadlineAt: z.string().nullable(),
  allowLockForever: z.boolean(),
  revealedAt: z.string().nullable(),
  myLockedAt: z.string().nullable(),
  /** Whether the viewer may still change (or make) their own pick. */
  editable: z.boolean(),
  /** Whether the viewer has earned the right to see everyone else's pick. */
  canRevealOthers: z.boolean(),
  revealed: z.array(questionRevealedPickSchema),

  // --- `multiple_choice` only; null/empty for `football` -----------------
  scoringMode: scoringModeSchema.nullable(),
  minPoints: z.number().nullable(),
  maxPoints: z.number().nullable(),
  /** How the answers are laid out for everyone — the organizer's choice, not the viewer's. */
  displayFormat: questionDisplayFormatSchema,
  /** How this question looks once revealed — a separate choice from `displayFormat`. */
  resultsFormat: questionResultsFormatSchema,
  options: z.array(questionOptionSchema),
  /** The viewer's own pick, if any — null if unanswered or not a participant. */
  myOptionId: z.string().nullable(),

  // --- `date` only; null for every other kind -----------------------------
  /** How closeness becomes points — see `dateScoringModeSchema`. */
  dateScoringMode: dateScoringModeSchema.nullable(),
  /** `YYYY-MM-DD` — which month the calendar should open on. Null means the current month. */
  dateTargetMonth: z.string().nullable(),
  /** The real moment, wall-clock `YYYY-MM-DD HH:MM:SS`. Null until revealed. */
  realDateAt: z.string().nullable(),
  /** The viewer's own guess, `YYYY-MM-DD`. Null if unanswered or not a participant. */
  myDateGuess: z.string().nullable(),

  // --- `football` only; null for `multiple_choice` ------------------------
  /** True when the real result comes from a linked real match automatically — the organizer cannot enter one manually. */
  isSourceLinked: z.boolean(),
  /** The source match's competition name, e.g. "La Liga 2026/27". Null for a fully custom match. */
  sourceCompetitionName: z.string().nullable(),
  homeTeamName: z.string().nullable(),
  awayTeamName: z.string().nullable(),
  canGoToPenalties: z.boolean(),
  realHome: z.number().nullable(),
  realAway: z.number().nullable(),
  realPenaltyWinner: penaltyWinnerSchema.nullable(),
  myHomeScore: z.number().nullable(),
  myAwayScore: z.number().nullable(),
  myPenaltyWinner: penaltyWinnerSchema.nullable(),
  /** The `MatchParams` this question scores against — "Advanced settings". Null for `multiple_choice`. */
  matchOutcome: z.number().nullable(),
  matchExact: z.number().nullable(),
  matchPotMultiplier: z.number().nullable(),
  matchDecay: z.number().nullable(),
  matchThreshold: z.number().nullable(),
  matchPenaltyExact: z.number().nullable(),
  matchPenaltyWrong: z.number().nullable(),
});

export const questionsForGroupSchema = z.object({
  isOrganizer: z.boolean(),
  isParticipant: z.boolean(),
  questions: z.array(questionSchema),
});

/** What the creation screen (and the later "add a question" screen) build up client-side before saving. */
export interface QuestionOptionDraft {
  label: string;
  /** Cosmetic, `card` display format only — a `#rrggbb` string, the API rejects anything else. */
  color?: string;
  emoji?: string;
  imageUrl?: string;
}

interface SharedDraftFields {
  /** Omit for "stays open until revealed". */
  deadlineAt?: string;
  allowLockForever?: boolean;
}

export interface MultipleChoiceDraft extends SharedDraftFields {
  kind: 'multiple_choice';
  prompt: string;
  scoringMode: z.infer<typeof scoringModeSchema>;
  options: QuestionOptionDraft[];
  /** Only meaningful when scoringMode is "per_answer". */
  minPoints?: number;
  maxPoints?: number;
  /** Omit for "list" — the layout every question had before this was configurable. */
  displayFormat?: z.infer<typeof questionDisplayFormatSchema>;
  /** Omit for "list". How the question looks once revealed. */
  resultsFormat?: z.infer<typeof questionResultsFormatSchema>;
}

/**
 * "Advanced settings": the same `MatchParams` a real match's ruleset stage
 * assigns it — see `questions.matchOutcome`'s doc comment
 * (`packages/db/src/schema/custom.ts`). All optional: omitted fields
 * default server-side to a group-stage-like rule, which is what lets this
 * stay a collapsed, opt-in section in the editor rather than seven
 * always-visible fields.
 */
interface FootballScoringDraft {
  matchOutcome?: number;
  matchExact?: number;
  matchPotMultiplier?: number;
  matchDecay?: number;
  matchThreshold?: number;
  matchPenaltyExact?: number;
  matchPenaltyWrong?: number;
}

/** A real match, browsed from our own database — never fetched live. */
export interface FootballFromDatabaseDraft extends SharedDraftFields, FootballScoringDraft {
  kind: 'football';
  source: 'database';
  sourceMatchId: string;
}

/** Entirely organizer-typed, with no link to real data — the organizer must type the real result in too, at reveal. */
export interface FootballCustomDraft extends SharedDraftFields, FootballScoringDraft {
  kind: 'football';
  source: 'custom';
  homeTeamName: string;
  awayTeamName: string;
  canGoToPenalties: boolean;
}

/**
 * "Guess the day this will happen" — no answers to author at all, only the
 * stakes: how closeness becomes points, and (for the paying modes) the range
 * it falls in. The real moment arrives at reveal.
 */
export interface DateDraft extends SharedDraftFields {
  kind: 'date';
  prompt: string;
  /** Omit for "placement" — ranks and medals, no points. */
  dateScoringMode?: z.infer<typeof dateScoringModeSchema>;
  /** Only read by the point-paying modes; "placement" ignores both. */
  minPoints?: number;
  maxPoints?: number;
  /** `YYYY-MM-DD` — which month the calendar opens on. Omit for the current month. */
  dateTargetMonth?: string;
  /** Omit for "leaderboard" — a ranked standings table is the point of this kind. */
  resultsFormat?: z.infer<typeof questionResultsFormatSchema>;
}

export type QuestionDraft =
  MultipleChoiceDraft | FootballFromDatabaseDraft | FootballCustomDraft | DateDraft;

export type QuestionPickInput =
  | { kind: 'multiple_choice'; optionId: string }
  | {
      kind: 'football';
      homeScore: number;
      awayScore: number;
      penaltyWinner?: z.infer<typeof penaltyWinnerSchema>;
    }
  /** `dateGuess` is `YYYY-MM-DD` — a calendar day, never a timestamp. */
  | { kind: 'date'; dateGuess: string };

export interface QuestionRulesPatch extends FootballScoringDraft {
  scoringMode?: z.infer<typeof scoringModeSchema>;
  minPoints?: number | null;
  maxPoints?: number | null;
  deadlineAt?: string | null;
  allowLockForever?: boolean;
  displayFormat?: z.infer<typeof questionDisplayFormatSchema>;
  resultsFormat?: z.infer<typeof questionResultsFormatSchema>;
  dateScoringMode?: z.infer<typeof dateScoringModeSchema>;
  dateTargetMonth?: string | null;
  /** Per-answer cosmetics. `null` clears a field; omitting it leaves that field alone. */
  optionAppearance?: OptionAppearanceInput[];
}

export interface OptionAppearanceInput {
  optionId: string;
  color?: string | null;
  emoji?: string | null;
  imageUrl?: string | null;
}

export interface OptionResultInput {
  optionId: string;
  resultStatus?: z.infer<typeof resultStatusSchema>;
  pointsAwarded?: number;
  isWinner?: boolean;
}

export interface FootballResultInput {
  realHome: number;
  realAway: number;
  realPenaltyWinner?: z.infer<typeof penaltyWinnerSchema>;
}

export interface DateResultInput {
  /**
   * Wall-clock `YYYY-MM-DDTHH:MM` — exactly what an
   * `<input type="datetime-local">` yields, sent through untouched. Never
   * `toISOString()`d: see `questions.realDateAt`'s doc comment for why an
   * instant here would make the ranking depend on the submitting browser.
   */
  realDateAt: string;
}

export const footballTeamOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  flagCode: z.string().nullable(),
  crestUrl: z.string().nullable(),
});

export const upcomingMatchOptionSchema = z.object({
  id: z.string(),
  homeTeamName: z.string(),
  awayTeamName: z.string(),
  kickoffAt: z.string().nullable(),
  canGoToPenalties: z.boolean(),
});

export type Competition = z.infer<typeof competitionSchema>;
export type GroupSummary = z.infer<typeof groupSummarySchema>;
export type GroupDetail = z.infer<typeof groupDetailSchema>;
export type PoolSummary = z.infer<typeof poolSummarySchema>;
export type PoolDetail = z.infer<typeof poolDetailSchema>;
export type JoinPreview = z.infer<typeof joinPreviewSchema>;
export type PendingInvite = z.infer<typeof pendingInviteSchema>;
export type Team = z.infer<typeof teamSchema>;
export type HomeSummary = z.infer<typeof homeSummarySchema>;
export type HomePick = z.infer<typeof homePickSchema>;
export type HomeResult = z.infer<typeof homeResultSchema>;
export type HomeStanding = z.infer<typeof homeStandingSchema>;

export type PredictionMatch = z.infer<typeof predictionMatchSchema>;
export type PredictionsForGroup = z.infer<typeof predictionsForGroupSchema>;
export type LeaderboardRow = z.infer<typeof leaderboardRowSchema>;
export type Leaderboard = z.infer<typeof leaderboardSchema>;
export type CustomLeaderboardRow = z.infer<typeof customLeaderboardRowSchema>;
export type CustomLeaderboard = z.infer<typeof customLeaderboardSchema>;
export type ProfileMatch = z.infer<typeof profileMatchSchema>;
export type ParticipantProfile = z.infer<typeof participantProfileSchema>;
export type PointsEvolution = z.infer<typeof pointsEvolutionSchema>;
export type MatchParams = z.infer<typeof matchParamsSchema>;
export type RulesStage = z.infer<typeof rulesStageSchema>;
export type RulesWorkedPrediction = z.infer<typeof rulesWorkedPredictionSchema>;
export type RulesExample = z.infer<typeof rulesExampleSchema>;
export type GroupRules = z.infer<typeof groupRulesSchema>;
export type ScoringMode = z.infer<typeof scoringModeSchema>;
export type QuestionOption = z.infer<typeof questionOptionSchema>;
export type QuestionItem = z.infer<typeof questionSchema>;
export type QuestionRevealedPick = z.infer<typeof questionRevealedPickSchema>;
export type QuestionsForGroup = z.infer<typeof questionsForGroupSchema>;
export type ResultStatus = z.infer<typeof resultStatusSchema>;
export type QuestionKind = z.infer<typeof questionKindSchema>;
export type QuestionDisplayFormat = z.infer<typeof questionDisplayFormatSchema>;
export type QuestionResultsFormat = z.infer<typeof questionResultsFormatSchema>;
export type DateScoringMode = z.infer<typeof dateScoringModeSchema>;
export type PenaltyWinner = z.infer<typeof penaltyWinnerSchema>;
export type FootballTeamOption = z.infer<typeof footballTeamOptionSchema>;
export type UpcomingMatchOption = z.infer<typeof upcomingMatchOptionSchema>;

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const api = {
  listCompetitions: () => request('/api/competitions', z.array(competitionSchema)),

  /** `since` is the viewer's own last-seen timestamp, kept client-side — see `routes/home.ts`. */
  getHome: (locale: string, since: string | null) =>
    request(
      `/api/home?locale=${encodeURIComponent(locale)}${since ? `&since=${encodeURIComponent(since)}` : ''}`,
      homeSummarySchema,
    ),

  listGroups: () => request('/api/groups', z.array(groupSummarySchema)),

  getGroup: (id: string) => request(`/api/groups/${id}`, groupDetailSchema),

  /**
   * Omitting `competitionId` creates a bare circle — just a name, ready to
   * invite people and add its first bolão whenever the organizer chooses
   * (PLAN.md Phase 10.18). `poolId` is null in that case. Passing it (the
   * older combined path, still used internally by nothing in this client but
   * kept working server-side) creates the first bolão in the same call.
   */
  createGroup: (body: {
    name: string;
    competitionId?: string;
    joinAs?: string;
    imageUrl?: string;
  }) =>
    request(
      '/api/groups',
      z.object({ id: z.string(), poolId: z.string().nullable(), joinToken: z.string() }),
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  /** Organizer-only: upload a cover image, shared by circles and bolões alike (PLAN.md Phase 10.19). Returns a URL to pass into `createGroup`/`createPool`/`updateGroupImage`/`updateBolaoImage`. */
  uploadGroupImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return requestMultipart('/api/group-images', z.object({ url: z.string() }), formData);
  },

  /** Organizer-only: set or clear a circle's cover image after creation. */
  updateGroupImage: (groupId: string, imageUrl: string | null) =>
    request(`/api/groups/${groupId}`, z.object({ ok: z.boolean() }), {
      method: 'PATCH',
      body: JSON.stringify({ imageUrl }),
    }),

  removeCircleMember: (groupId: string, memberId: string) =>
    request(`/api/groups/${groupId}/members/${memberId}`, z.object({ ok: z.boolean() }), {
      method: 'DELETE',
    }),

  /** Organizer-only: delete the circle entirely — cascades to every bolão inside it. */
  deleteGroup: (groupId: string) =>
    request(`/api/groups/${groupId}`, z.object({ ok: z.boolean() }), { method: 'DELETE' }),

  // --- bolões ------------------------------------------------------------

  listPools: (groupId: string) =>
    request(`/api/groups/${groupId}/bolaos`, z.array(poolSummarySchema)),

  getPool: (groupId: string, poolId: string) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}`, poolDetailSchema),

  /** Organizer-only: add a new football bolão to an existing circle. */
  createPool: (
    groupId: string,
    body: {
      name: string;
      competitionId: string;
      imageUrl?: string;
      teamIds?: string[];
      allowLockForever?: boolean;
    },
  ) =>
    request(`/api/groups/${groupId}/bolaos`, z.object({ id: z.string() }), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Organizer-only: add a new custom bolão to an existing circle. */
  createCustomPool: (
    groupId: string,
    body: { name: string; questions: QuestionDraft[]; imageUrl?: string },
  ) =>
    request(`/api/groups/${groupId}/bolaos/custom`, z.object({ id: z.string() }), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Organizer-only: set or clear a bolão's cover image after creation. */
  updateBolaoImage: (groupId: string, poolId: string, imageUrl: string | null) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}`, z.object({ ok: z.boolean() }), {
      method: 'PATCH',
      body: JSON.stringify({ imageUrl }),
    }),

  /** Organizer-only: the bolão settings screen — currently just `allowLockForever`. */
  updateBolaoSettings: (groupId: string, poolId: string, body: { allowLockForever: boolean }) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}/settings`, z.object({ ok: z.boolean() }), {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** A circle member opts into a specific bolão. */
  joinPool: (groupId: string, poolId: string, displayName: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/join`,
      z.object({ participantId: z.string() }),
      { method: 'POST', body: JSON.stringify({ displayName }) },
    ),

  /** Organizer-only: remove someone from just this bolão's roster — their circle membership and other bolões are untouched. */
  removePoolParticipant: (groupId: string, poolId: string, participantId: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/participants/${participantId}`,
      z.object({ ok: z.boolean() }),
      { method: 'DELETE' },
    ),

  /** Organizer-only: delete this bolão — leaves the circle and its other bolões untouched. */
  deletePool: (groupId: string, poolId: string) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}`, z.object({ ok: z.boolean() }), {
      method: 'DELETE',
    }),

  // --- custom competitions ---------------------------------------------------

  createCustomGroup: (body: { name: string; joinAs?: string; questions: QuestionDraft[] }) =>
    request(
      '/api/groups/custom',
      z.object({ id: z.string(), poolId: z.string(), joinToken: z.string() }),
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  getQuestions: (groupId: string, poolId: string) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}/questions`, questionsForGroupSchema),

  /** Organizer-only: append one more question to an already-created custom competition. */
  addQuestion: (groupId: string, poolId: string, draft: QuestionDraft) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}/questions`, questionSchema, {
      method: 'POST',
      body: JSON.stringify(draft),
    }),

  /** Organizer-only: remove a question — its options, everyone's picks, and its score. */
  deleteQuestion: (groupId: string, poolId: string, questionId: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/questions/${questionId}`,
      z.object({ ok: z.boolean() }),
      {
        method: 'DELETE',
      },
    ),

  saveQuestionPrediction: (
    groupId: string,
    poolId: string,
    questionId: string,
    pick: QuestionPickInput,
  ) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/questions/${questionId}/prediction`,
      z.object({
        optionId: z.string().nullable(),
        homeScore: z.number().nullable(),
        awayScore: z.number().nullable(),
        penaltyWinner: penaltyWinnerSchema.nullable(),
      }),
      { method: 'POST', body: JSON.stringify(pick) },
    ),

  /** "Lock forever" — only when the question's allowLockForever is true. */
  lockQuestionPrediction: (groupId: string, poolId: string, questionId: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/questions/${questionId}/lock`,
      z.object({ lockedAt: z.string() }),
      { method: 'POST' },
    ),

  /** Organizer-only: edit a question's scoring mode, point range, deadline, or lock permission. */
  updateQuestionRules: (
    groupId: string,
    poolId: string,
    questionId: string,
    patch: QuestionRulesPatch,
  ) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/questions/${questionId}`,
      z.object({ ok: z.boolean() }),
      {
        method: 'PATCH',
        body: JSON.stringify(patch),
      },
    ),

  /** Organizer-only: enter real results for a `multiple_choice` question, revealing it. */
  revealQuestionResults: (
    groupId: string,
    poolId: string,
    questionId: string,
    results: OptionResultInput[],
  ) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/questions/${questionId}/reveal`,
      z.object({ ok: z.boolean() }),
      { method: 'POST', body: JSON.stringify({ results }) },
    ),

  /** Organizer-only: enter the real result for a `football` question, revealing it. */
  revealFootballQuestionResult: (
    groupId: string,
    poolId: string,
    questionId: string,
    football: FootballResultInput,
  ) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/questions/${questionId}/reveal`,
      z.object({ ok: z.boolean() }),
      { method: 'POST', body: JSON.stringify({ football }) },
    ),

  /** Organizer-only: enter the real moment for a `date` question, revealing it and ranking every guess. */
  revealDateQuestionResult: (
    groupId: string,
    poolId: string,
    questionId: string,
    date: DateResultInput,
  ) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/questions/${questionId}/reveal`,
      z.object({ ok: z.boolean() }),
      { method: 'POST', body: JSON.stringify({ date }) },
    ),

  /**
   * Upload the image for one answer of a `card`-format question. Not
   * bolão-scoped: answers are configured on the creation screen, before the
   * circle or the question exist (see `apps/api/src/custom/images.ts`).
   */
  uploadQuestionImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return requestMultipart('/api/question-images', z.object({ url: z.string() }), formData);
  },

  // --- football-question authoring lookups (own database only) --------------

  listFootballTeams: (competitionId: string, locale: string) =>
    request(
      `/api/football-competitions/${competitionId}/teams?locale=${encodeURIComponent(locale)}`,
      z.object({ teams: z.array(footballTeamOptionSchema) }),
    ),

  listUpcomingMatchesForTeam: (competitionId: string, teamId: string, locale: string) =>
    request(
      `/api/football-competitions/${competitionId}/teams/${teamId}/upcoming-matches?locale=${encodeURIComponent(locale)}`,
      z.object({ matches: z.array(upcomingMatchOptionSchema) }),
    ),

  // --- "anyone with the link" ---------------------------------------------

  /** Mint a fresh link, invalidating any previous one. Token returned once. */
  regenerateJoinLink: (groupId: string) =>
    request(`/api/groups/${groupId}/link`, z.object({ token: z.string() }), { method: 'POST' }),

  disableJoinLink: (groupId: string) =>
    request(`/api/groups/${groupId}/link`, z.object({ ok: z.boolean() }), { method: 'DELETE' }),

  previewJoin: (token: string) =>
    request(`/api/join/${encodeURIComponent(token)}`, joinPreviewSchema),

  /** Joins the circle only — which competitions you actually play is a separate opt-in per pool. */
  joinViaLink: (token: string, displayName: string) =>
    request(
      `/api/join/${encodeURIComponent(token)}`,
      z.object({ memberId: z.string(), groupId: z.string() }),
      { method: 'POST', body: JSON.stringify({ displayName }) },
    ),

  // --- playing without an account (PLAN.md Phase 10.29) --------------------

  /** Who is already in this circle, for the "pick yourself" list. No session required. */
  getGuestRoster: (token: string) =>
    request(`/api/join/${encodeURIComponent(token)}/roster`, guestRosterSchema),

  /**
   * Register a new accountless player and sign in as them in one step — they
   * chose the PIN seconds ago, so asking for it again proves nothing.
   */
  createGuest: (token: string, body: { displayName: string; pin: string; avatarUrl?: string }) =>
    request(
      `/api/join/${encodeURIComponent(token)}/guests`,
      z.object({ groupId: z.string(), memberId: z.string() }),
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Enter an existing player's PIN and become them. */
  signInGuest: (token: string, memberId: string, pin: string) =>
    request(
      `/api/join/${encodeURIComponent(token)}/guests/${memberId}/session`,
      z.object({ groupId: z.string() }),
      { method: 'POST', body: JSON.stringify({ pin }) },
    ),

  /** Claim an entry that has no PIN yet — a fresh one, or one the organizer reset. */
  setGuestPin: (token: string, memberId: string, pin: string) =>
    request(
      `/api/join/${encodeURIComponent(token)}/guests/${memberId}/pin`,
      z.object({ groupId: z.string() }),
      { method: 'POST', body: JSON.stringify({ pin }) },
    ),

  /** The photo on the registration form, uploaded before the player row exists. */
  uploadGuestAvatar: (token: string, file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return requestMultipart(
      `/api/join/${encodeURIComponent(token)}/guest-avatar`,
      z.object({ url: z.string() }),
      formData,
    );
  },

  getGuestSession: () => request('/api/guest/session', guestSessionSchema),

  signOutGuest: () =>
    request('/api/guest/sign-out', z.object({ ok: z.boolean() }), { method: 'POST' }),

  /** Move a guest's photo, memberships and predictions onto the account that just signed in. */
  upgradeGuest: () =>
    request('/api/guest/upgrade', z.object({ groupId: z.string() }), { method: 'POST' }),

  /** Organizer-only: clear a guest's PIN so they can choose a new one. */
  resetGuestPin: (groupId: string, memberId: string) =>
    request(`/api/groups/${groupId}/members/${memberId}/pin-reset`, z.object({ ok: z.boolean() }), {
      method: 'POST',
    }),

  // --- invitations by email ------------------------------------------------

  inviteByEmail: (groupId: string, email: string) =>
    request(`/api/groups/${groupId}/invites`, z.object({ inviteId: z.string() }), {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  revokeInvite: (groupId: string, inviteId: string) =>
    request(`/api/groups/${groupId}/invites/${inviteId}`, z.object({ ok: z.boolean() }), {
      method: 'DELETE',
    }),

  listPendingInvites: () => request('/api/invites/pending', z.array(pendingInviteSchema)),

  acceptInvite: (inviteId: string, displayName: string) =>
    request(
      `/api/invites/${inviteId}/accept`,
      z.object({ memberId: z.string(), groupId: z.string() }),
      { method: 'POST', body: JSON.stringify({ displayName }) },
    ),

  // --- predictions ----------------------------------------------------------

  getPredictions: (groupId: string, poolId: string, locale: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/predictions?locale=${encodeURIComponent(locale)}`,
      predictionsForGroupSchema,
    ),

  savePrediction: (
    groupId: string,
    poolId: string,
    matchId: string,
    body: { home: number; away: number; penaltyWinner: 'home' | 'away' | null },
  ) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/predictions/${matchId}`,
      z.object({
        home: z.number(),
        away: z.number(),
        penaltyWinner: z.enum(['home', 'away']).nullable(),
      }),
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  /** "Lock forever" — irreversible; see PredictionsScreen for the confirm step. */
  lockPrediction: (groupId: string, poolId: string, matchId: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/predictions/${matchId}/lock`,
      z.object({ lockedAt: z.string() }),
      { method: 'POST' },
    ),

  // DEV-ONLY: clears every lock the caller made in this group, so locking can
  // be tested repeatedly without re-seeding. The API route this calls is
  // itself excluded outside development — see routes/predictions.ts.
  unlockAllPredictionsDev: (groupId: string, poolId: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/predictions/unlock-all`,
      z.object({ unlocked: z.number() }),
      {
        method: 'POST',
      },
    ),

  // --- leaderboard ------------------------------------------------------------

  getLeaderboard: (groupId: string, poolId: string) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}/leaderboard`, leaderboardSchema),

  getCustomLeaderboard: (groupId: string, poolId: string) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}/custom-leaderboard`, customLeaderboardSchema),

  // --- profile & chart ----------------------------------------------------

  getParticipantProfile: (groupId: string, poolId: string, participantId: string, locale: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/participants/${participantId}/profile?locale=${encodeURIComponent(locale)}`,
      participantProfileSchema,
    ),

  getPointsEvolution: (groupId: string, poolId: string) =>
    request(`/api/groups/${groupId}/bolaos/${poolId}/points-evolution`, pointsEvolutionSchema),

  // --- account -------------------------------------------------------------

  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return requestMultipart('/api/account/avatar', z.object({ image: z.string() }), formData);
  },

  removeAvatar: () =>
    request('/api/account/avatar', z.object({ ok: z.boolean() }), { method: 'DELETE' }),

  // --- rules ----------------------------------------------------------------

  getRules: (groupId: string, poolId: string, locale: string) =>
    request(
      `/api/groups/${groupId}/bolaos/${poolId}/rules?locale=${encodeURIComponent(locale)}`,
      groupRulesSchema,
    ),
};
